import * as models from '@/datastore/models'

import { GroceryStore } from '@/store/modules/grocery'

export namespace V1 {

	export function serialize(state: GroceryStore): Schema {
		return {
			'(brands+pantry-items)': serializeBrand_PantryItems(state.brands_pantryItemsCollection),
			'(categories+lists)': serializeCategory_Lists(state.categories_listsCollection),
			'(categories+pantry-items)': serializeCategory_PantryItems(state.categories_pantryItemsCollection),
			'brands': serializeBrands(state.brandsCollection),
			'categories': serializeCategories(state.categoriesCollection),
			'coupons': serializeCoupons(state.couponsCollection),
			'lists': serializeLists(state.listsCollection),
			'pantry-items': serializePantryItems(state.pantryItemsCollection),
			'shopping-list-items': serializeShoppingListItems(state.shoppingListItemsCollection),
			'version': '1',
		}
	}

	export interface Brand {
		name: string
	}

	export interface Category {
		color: string
		icon: string
		name: string
	}

	export interface Coupon {
		description: string
		expirationDate: number
		pantryItemId: string
		promoItems: PromoItem[]
		title: string
	}

	export interface List {
		name: string
	}

	export interface PantryItem {
		name: string
		notes: string
	}

	export interface PromoItem {
		brandId?: string
		categoryId?: string
		pantryItemId?: string
	}

	export interface ShoppingListItem {
		createdAt?: number
		createdBy?: string
		name: string
		notes?: string
		preferredBrands?: string[]
		quantity?: number
	}

	export interface Schema {
		'(brands+pantry-items)': [string, string][]
		'(categories+lists)': [string, string][]
		'(categories+pantry-items)': [string, string][]
		'brands': Dictionary<Brand>
		'categories': Dictionary<Category>
		'coupons': Dictionary<Coupon>
		'lists': Dictionary<List>
		'pantry-items': Dictionary<PantryItem>
		'shopping-list-items': Dictionary<ShoppingListItem>
		'version': '1'
	}

	function serializeBrands(brands: Dictionary<models.Brand>): Dictionary<Brand> {
		return Object.entries(brands)
			.reduce((x, [$id, { name }]) => ({ ...x, [$id]: { name } }), {})
	}

	function serializeBrand_PantryItems(relationships: Dictionary<models.Brand_PantryItem>): [string, string][] {
		return Object.values(relationships)
			.map(({ brandId, pantryItemId }): [string, string] => [brandId, pantryItemId])
	}

	function serializeCategories(categories: Dictionary<models.Category>): Dictionary<Category> {
		return Object.entries(categories)
			.reduce((x, [$id, { color, icon, name }]) => ({ ...x, [$id]: { color, icon, name } }), {})
	}

	function serializeCoupons(coupons: Dictionary<models.Coupon>): Dictionary<Coupon> {
		return Object.entries(coupons)
			.reduce((x, [$id, { description, expirationDate, promoItems, title }]) => ({
				...x,
				[$id]: <Coupon>{
					description,
					expirationDate,
					promoItems,
					title,
				},
			}), {} as Dictionary<Coupon>)
	}

	function serializeCategory_Lists(relationships: Dictionary<models.Category_List>): [string, string][] {
		return Object.values(relationships)
			.map(({ categoryId, listId }): [string, string] => [categoryId, listId])
	}

	function serializeCategory_PantryItems(relationships: Dictionary<models.Category_PantryItem>): [string, string][] {
		return Object.values(relationships)
			.map(({ categoryId, pantryItemId }): [string, string] => [categoryId, pantryItemId])
	}

	function serializeLists(lists: Dictionary<models.List>): Dictionary<List> {
		return Object.entries(lists)
			.reduce((x, [$id, { name }]) => ({ ...x, [$id]: { name } }), {})
	}

	function serializePantryItems(pantryItems: Dictionary<models.PantryItem>): Dictionary<PantryItem> {
		return Object.entries(pantryItems)
			.reduce((x, [$id, { name, notes }]) => ({ ...x, [$id]: { name, notes } }), {})
	}

	function serializeShoppingListItems(shoppingListItems: Dictionary<models.ShoppingListItem>): Dictionary<ShoppingListItem> {
		return Object.entries(shoppingListItems)
			.reduce((x, [$id, { createdAt, createdBy, name, notes, pantryItemId, preferredBrands, quantity }]) =>
				({ ...x, [$id]: { createdAt, createdBy, name, notes, pantryItemId, preferredBrands, quantity } }),
				{},
			)
	}

}
